import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';

import { BehaviorSubject, EMPTY, Observable, of, Subject } from 'rxjs';

import { switchMap } from 'rxjs/operators';
import { App, DocTemplate, Project } from '../models';
import { BulkDocTemplate } from '../models/bulk-templates';
import { SNACKBAR_CONSTANTS } from '../shared/constants';
import { DocumentTemplateDataService } from './data-services/document-template-data.service';
import { ProjectsService } from './projects.service';

@Injectable({
    providedIn: 'root'
})
export class DocumentTemplateService {
    private batchTemplatePosted: Subject<boolean> = new Subject<boolean>();
    private batchBulkTemplatesLoaded: Subject<boolean> = new Subject<boolean>();
    private docTemplateModified: Subject<boolean> = new Subject<boolean>();
    private docTemplatesLoaded: Subject<boolean> = new Subject<boolean>();
    private docTemplatePosted: Subject<boolean> = new Subject<boolean>();

    batchTemplatePosted$ = this.batchTemplatePosted.asObservable();
    batchBulkTemplatesLoaded$ = this.batchBulkTemplatesLoaded.asObservable();
    docTemplatesLoaded$ = this.docTemplatesLoaded.asObservable();
    docTemplatePosted$ = this.docTemplatePosted.asObservable();
    docTemplateModified$ = this.docTemplateModified.asObservable();

    documentTemplates: DocTemplate[] = [];

    bulkDocTemplates: BulkDocTemplate[] = [];

    DocTemplates: BehaviorSubject<DocTemplate[]> = new BehaviorSubject<DocTemplate[]>(null);
    DocTemplate: BehaviorSubject<DocTemplate> = new BehaviorSubject<DocTemplate>(null);
    newDocTemplate: BehaviorSubject<string> = new BehaviorSubject<string>(null);
    DocTemplateLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    GeneratingDocTemplate = new Subject<boolean>();

    currentDocTemplate: DocTemplate;
    currentAppDocTemplate: DocTemplate;

    get currentProject(): Project {
        return this._projectsService.model.currentProject;
    }

    constructor(
        private _snackBar: MatSnackBar,
        private _docTemplateDataService: DocumentTemplateDataService,
        private _projectsService: ProjectsService,
    ) {
        if (sessionStorage.getItem("DocTemplate")) {
            const DocTemplate = JSON.parse(sessionStorage.getItem("DocTemplate"));
            this.setDocTemplate(DocTemplate);
        }
    }

    setDocTemplate(docTemplate: DocTemplate): void {
        this.setCurrentDocTemplate(docTemplate);
        sessionStorage.setItem("DocTemplate", JSON.stringify(docTemplate));
    }

    private setCurrentDocTemplate(docTemplate: DocTemplate): void {
        this.DocTemplate.next(docTemplate);
        this.currentDocTemplate = docTemplate;
    }

    resetDocTemplates(): void {
        this.DocTemplates.next(this.documentTemplates);
    }

    setDocTemplates(docTemplates: any): void {
        this.documentTemplates = docTemplates;
        sessionStorage.setItem('DocTemplates', JSON.stringify(docTemplates));
        this.DocTemplates.next(docTemplates);
        this.docTemplatesLoaded.next(true);
    }

    createDocTemplate(app: App, docTemplate: DocTemplate): void {
        this._docTemplateDataService.createDocTemplate(app, docTemplate)
            .subscribe({
                next: ((result: DocTemplate) => {
                    this.postDocTemplateFile(app, result, docTemplate.docTemplateFile);
                }),
            });
    }

    updateDocTemplate(app: App, docTemplate: DocTemplate): void {
        this._docTemplateDataService.updateDocTemplate(app, docTemplate)
            .subscribe({
                next: ((result: DocTemplate) => {
                    if (docTemplate.docTemplateFile) {
                        this.postDocTemplateFile(app, result, docTemplate.docTemplateFile);
                        return;
                    }
                    this.updateDocTemplateLocally(result);
                    this.getDocTemplatesByProjId(app, docTemplate.projectId);
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_SUCCESS('Document Template', 'saved'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                }),
                error: (() => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR('Document Template', 'creating'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                })
            })
            .add(() => {
                this.docTemplateModified.next(true);
            });
    }

    deleteDocTemplate(app: App, docTemplate: DocTemplate): void {
        this._docTemplateDataService.deleteDocTemplate(app, docTemplate).subscribe({
            next: (() => {
                this.getDocTemplatesByProjId(app, docTemplate.projectId);
                this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_SUCCESS('Document Template', 'deleted'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
            }),
            error: (() => {
                this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR('Document Template', 'deleting'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
            })
        }).add(() => {
            this.docTemplateModified.next(true);

        });
    }

    changeActiveDocTemplate(app: App, docTemplate: DocTemplate): void {
        this._docTemplateDataService.changeActiveDocTemplate(app, docTemplate)
            .subscribe({
                next: (() => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_TOGGLE_ACTIVE('Document Template', docTemplate.docTemplateName, docTemplate.active), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                }),
                error: (() => {
                    docTemplate.active = !docTemplate.active;
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR('Document Template', 'updating'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                })
            });
    }

    postDocTemplateFile(app: App, docTemplate: DocTemplate, docTemplateFile: any): void {
        let isNew = false;
        const formData = new FormData();
        formData.append("file", docTemplateFile.rawFile);

        if (!docTemplate.filePath) {
            isNew = true;
        }

        this._docTemplateDataService.postDocTemplateFile(app, docTemplate, isNew, formData)
            .subscribe({
                next: (response: DocTemplate) => {
                    docTemplateFile.fileName = response.fileName;
                    docTemplateFile.filePath = response.filePath;
                    this.updateDocTemplateLocally(response);
                    this.getDocTemplatesByProjId(app, docTemplate.projectId);
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_SUCCESS('Document Template', 'saved'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                    this.docTemplateModified.next(true);
                }
            });
    }

    postBulkTemplate(appId: string, bulkTemplate: BulkDocTemplate): void {
        this._docTemplateDataService.createBulkTemplate(appId, bulkTemplate)
            .subscribe({
                next: ((result: BulkDocTemplate) => {
                    this.bulkDocTemplates.push(result);
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_SUCCESS('Document Template Batch', 'created'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                }),
                error: (() => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR('Document Template Batch', 'creating'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                }),
                complete: (() => { }),
            })
            .add(() => {
                this.batchTemplatePosted.next(true);
            });
    }

    getBatchDocumentTemplatesByProjectId(app: App, project: Project): void {
        if (!app?.showDocumentTemplate) {
            return;
        }

        if (!project) {
            return;
        }

        this._docTemplateDataService.getBatchDocumentTemplateByProjectId(app, project)
            .subscribe({
                next: ((result: BulkDocTemplate[]) => {
                    result.forEach((documentTemplate: BulkDocTemplate) => {
                        if (!this.bulkDocTemplates.some((batchDocumentTemplate: BulkDocTemplate) => batchDocumentTemplate.id === documentTemplate.id)) {
                            this.bulkDocTemplates.push(documentTemplate);
                        }
                    });
                }),
                error: (() => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR('Batch Document Template', 'getting', true), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                }),
                complete: (() => { }),
            })
            .add(() => {
                this.batchBulkTemplatesLoaded.next(true);
            });
    }

    processedStatus(app: App, project: Project): void {
        this._docTemplateDataService.processedStatus(app, project)
            .subscribe({
                next: ((processedBatches: BulkDocTemplate[]) => {
                    processedBatches.forEach((processedBulkTemplate: BulkDocTemplate) => {
                        const batchInModel = this.bulkDocTemplates.some((batchDocumentTemplate: BulkDocTemplate) => batchDocumentTemplate.id === processedBulkTemplate.id);
                        if (batchInModel) {
                            this.bulkDocTemplates = this.bulkDocTemplates.map((bulkDoc: BulkDocTemplate) => {
                                if (bulkDoc.id === processedBulkTemplate.id) {
                                    bulkDoc = processedBulkTemplate;
                                }
                                return bulkDoc;
                            });

                        }
                    });
                }),
                error: (() => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR('Batch Document Template', 'getting status', true), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                }),
                complete: (() => { }),
            })
            .add(() => {
                this.batchBulkTemplatesLoaded.next(true);
            });

    }

    getDocTemplatesByProjId(app: App, projId: string): void {
        if (!app.showDocumentTemplate) {
            return;
        }

        this.loadDocTemplates(this._docTemplateDataService.getDocumentTemplates(app.id, projId), projId);
    }

    getSummaryProjectDocTemplates(currentApp: App, projectId: string | undefined): void {
        if (!currentApp.showDocumentTemplate || !projectId) {
            return;
        }

        this.loadDocTemplates(this._docTemplateDataService.getSummaryProjectDocumentTemplates(currentApp.id, projectId), projectId);
    }

    private loadDocTemplates(documentRequest$: Observable<DocTemplate[]>, projectId: string): void {
        this.DocTemplateLoading.next(true);

        documentRequest$
            .pipe(switchMap((result: DocTemplate[]) => {
                if (projectId != this.currentProject?.id) {
                    return EMPTY;
                }
                return of(result);
            }))
            .subscribe({
                next: ((result: DocTemplate[]) => {
                    this.setDocTemplates(result);
                }),
                error: (() => {
                    this.documentTemplates = [];
                    this.disconnectDocTemplate();
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR('Document Template', 'getting', true), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                }),
                complete: (() => { }),
            })
            .add(() => {
                this.DocTemplateLoading.next(false);
            });
    }

    generateDocumentFromTemplate(app: App, project: Project, siteId: string, docTemplateId: string, fileName: string): void {
        this.GeneratingDocTemplate.next(true);

        const documentRequest$ = project.isSummaryProject === true
            ? this._docTemplateDataService.generateSummaryProjectDocumentFromTemplate(app, project, siteId, docTemplateId)
            : this._docTemplateDataService.generateDocumentFromTemplate(app, project, siteId, docTemplateId);

        documentRequest$
            .subscribe({
                next: (response: Blob) => {
                    const blob = new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });

                    const a = document.createElement('a');
                    a.download = fileName;
                    a.href = window.URL.createObjectURL(blob);
                    a.click();
                },
                error: () => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR('Document Template', 'getting document'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                }
            })
            .add(() => {
                this.GeneratingDocTemplate.next(false);
            });
    }

    updateDocTemplateLocally(docTemplate: DocTemplate): void {
        let index;
        if ((index = this.documentTemplates.findIndex(u => u.id === docTemplate.id)) !== -1) {
            this.documentTemplates[index] = docTemplate;
        }
        else {
            this.documentTemplates.push(docTemplate);
        }
        this.resetDocTemplates();
    }

    disconnectDocTemplate(): void {
        this.setCurrentDocTemplate(null);
        sessionStorage.removeItem("DocTemplate");
    }
}
